import CryptoJS from "crypto-js";
import * as XLSX from "xlsx";

export function isImageFile(extension: string) {
  const imageExtensions = [
    ".jpg",
    "JPG",
    ".JPG",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    "PNG",
    "JPEG",
    "png",
    "jpg",
    ".PNG",
  ];
  return imageExtensions.includes(extension);
}

export function isDate(value: any) {
  if (!isNaN(Date.parse(value)) && typeof value === "string") {
    return true;
  }
  return false;
}

// export const formatNumber = (number: number, fixedDigits = 2): string => {
//   return number.toFixed(fixedDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
// };

export const onKeyDown = (event) => {
  if (event.key === "Enter") {
    event.preventDefault();
  }
};

export const encryptPassword = (password: string): string => {
  const iv = CryptoJS.enc.Base64.parse(
    process.env.REACT_APP_ENCRYPTION_IV || ""
  );
  const key = CryptoJS.enc.Base64.parse(
    process.env.REACT_APP_ENCRYPTION_KEY || ""
  );

  const encrypted = CryptoJS.AES.encrypt(password, key, {
    iv: iv,
    mode: CryptoJS.mode.CTR,
    padding: CryptoJS.pad.NoPadding,
  }).toString();

  return encrypted;
};

export const formatNumber = (
  number: number,
  fixedDigits = 3,
  addCurly = true
): string => {
  if (typeof number !== "number" || isNaN(number)) {
    return "0";
  }

  const isNegative = number < 0;
  const absoluteNumber = Math.abs(number);
  const fixedNumber = absoluteNumber?.toFixed(fixedDigits);

  const [integerPart, decimalPart] = fixedNumber?.split(".");

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formattedNumber = decimalPart
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;

  // Wrap the result in parentheses if the number is negative
  return isNegative && addCurly ? `(${formattedNumber})` : formattedNumber;
};

export function getGroupByData(data: any[], groupKey: string) {
  const groupedData = data?.reduce((acc: any, obj: any) => {
    const key = obj[groupKey];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});

  if (groupedData?.CSH) {
    return {
      CSH: groupedData.CSH,
      ...groupedData,
    };
  } else {
    return groupedData;
  }
}

export function getDashboardSumission(data: any[], sumKey: string) {
  return data.reduce(
    (accumulator, currentValue) =>
      Math.abs(accumulator) + Math.abs(currentValue[sumKey]),
    0
  );
}

export function getPortfolioStatus(statusCode: number) {
  if (statusCode >= 100 && statusCode < 200) {
    return "Active";
  } else if (statusCode >= 200 && statusCode < 300) {
    return "Blocked";
  } else if (statusCode >= 500 && statusCode < 600) {
    return "Closed";
  } else if (statusCode == 999) {
    return "Rejected";
  } else if (statusCode == 0) {
    return "Pending";
  } else {
    return "";
  }
}

export function convertCurrency(
  value: any,
  fromCurrency: any,
  toCurrency: any,
  rates: any
): number | null {
  if (fromCurrency === toCurrency) {
    return value;
  }

  // Check if direct conversion is available
  const directRate = getSpotRate(fromCurrency, toCurrency, rates);
  if (directRate !== null) {
    return value * directRate;
  }

  // If direct conversion is not available, go through KWD
  const toKwdRate = getSpotRate(fromCurrency, "KWD", rates);
  const fromKwdRate = getSpotRate("KWD", toCurrency, rates);

  if (toKwdRate !== null && fromKwdRate !== null) {
    const valueInKWD = value * toKwdRate;
    return valueInKWD * fromKwdRate;
  }

  return null;
}

function getSpotRate(
  fromCurrency: string,
  toCurrency: string,
  rates: any
): number | null {
  const rate = rates.find(
    (rate) =>
      (rate.FIRST_CURRENCY === fromCurrency &&
        rate.SECOND_CURRENCY === toCurrency) ||
      (rate.FIRST_CURRENCY === toCurrency &&
        rate.SECOND_CURRENCY === fromCurrency)
  );

  if (!rate) {
    return null;
  }

  if (rate.FIRST_CURRENCY === fromCurrency) {
    return rate.SPOT;
  } else {
    return 1 / rate.SPOT;
  }
}

export function getPortfolioTypeTranslation(type: string) {
  switch (type) {
    case "Managed by Company":
      return "Managed by Company";
    case "Managed by Client":
      return "Managed by Client";
    case "Managed by Client+":
      return "Managed by Client+";
    case "Managed by Company+":
      return "Managed by Company+";
    case "Custody":
      return "Custody";
    case "Custody Trustee":
      return "Custody Trustee";
    case "Held In Client Name":
      return "Held In Client Name";
    case "Assets Received in Kind":
      return "Assets Received in Kind";
    case "Fund":
      return "Fund";
    default:
      return "Uncategorized";
  }
}

export const getSumission = (
  data: any,
  key: string,
  currency?: string,
  fxRates: any = []
): number => {
  if (currency) {
    return data?.reduce(
      (accumulator: any, currentValue: any) =>
        accumulator +
        convertCurrency(
          currentValue[key],
          currentValue.reportingCurrency || currentValue.currency,
          currency,
          fxRates
        ),
      0
    );
  } else {
    return data?.reduce(
      (accumulator: any, currentValue: any) => accumulator + currentValue[key],
      0
    );
  }
};

export const generateExcel = (data: any, name: string) => {
  const workbook = XLSX.utils.book_new();

  data.forEach((sheetData: any) => {
    const { sheetName, sheetContent } = sheetData;
    const worksheet = XLSX.utils.json_to_sheet(sheetContent);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  const excelData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  const blob = new Blob([excelData], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const extractToEntity = (text: string) => {
  const toIndex = text.toLowerCase().indexOf("to ");
  if (toIndex === -1) return null;

  const afterTo = text.slice(toIndex + 3); // Skip "to "

  // Match up to the FIRST period that is followed by a space and an uppercase letter (to avoid cutting in abbreviations like K.S.C)
  const match = afterTo.match(/^(.*?\.(?:\s+[A-Z]|$))/);
  if (!match) return null;

  return match[1]
    .trim()
    .replace(/\s+[A-Z]$/, "")
    .trim();
};

export function getStandingDataOptions(
  data: any,
  title: string,
  branch: string[],
  needOther: boolean = false
) {
  const options =
    data?.[title]
      ?.filter((item: any) => branch.includes(item.branch))
      ?.map((item: any) => ({
        label: item.description,
        value: item.id,
      })) || [];
  if (needOther) {
    options.push({
      label: "Other",
      value: -1,
    });
  }
  return options;
}
